import React, { useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';

export interface TriageFilterButtonProps {
  open: boolean;
  toggle: () => void;
  active?: boolean;
  lead?: React.ReactNode;
  label: string;
  count?: number | string | null;
}

export function TriageFilterButton({ open, toggle, active, lead, label, count }: TriageFilterButtonProps) {
  return (
    <button
      type="button"
      onClick={toggle}
      className={cn(
        'inline-flex h-[30px] min-w-0 max-w-full shrink cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 font-[var(--font-body)] text-xs font-semibold tracking-[-0.005em] shadow-[var(--sh-1)]',
        active
          ? 'border-[var(--cream-shadow)] bg-[var(--surface-mute)] text-[var(--ink-0)]'
          : 'border-[var(--cream-2)] bg-[var(--cream-1)] text-[var(--ink-1)]',
      )}
    >
      {lead}
      <span className="min-w-0 truncate">{label}</span>
      {count != null && count !== '' ? (
        <span
          className={cn(
            'v6-mono shrink-0 rounded-full px-[5px] text-[10px] font-bold leading-[1.7]',
            active ? 'bg-[var(--ink-0)] text-[var(--cream-1)]' : 'bg-[var(--cream-0)] text-[var(--ink-2)]',
          )}
        >
          {count}
        </span>
      ) : null}
      <Icon
        kind="down"
        size={11}
        stroke={2}
        color="var(--ink-3)"
        className={cn('shrink-0 transition-transform duration-150', open && 'rotate-180')}
      />
    </button>
  );
}

export interface TriageFilterCheckRowProps {
  checked: boolean;
  onClick: () => void;
  label: string;
  sub?: string;
  count?: number;
  multi?: boolean;
  dot?: string;
  lead?: React.ReactNode;
}

export function TriageFilterCheckRow({
  checked,
  onClick,
  label,
  sub,
  count,
  multi = true,
  dot,
  lead,
}: TriageFilterCheckRowProps) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onClick();
      }}
      className="ti-menu-item flex cursor-pointer items-center gap-2.5"
      style={{ background: checked && !multi ? 'color-mix(in srgb, var(--success) 7%, transparent)' : 'transparent' }}
    >
      <span
        className={cn(
          'inline-flex h-4 w-4 shrink-0 items-center justify-center text-white',
          multi ? 'rounded-[5px]' : 'rounded-full',
          checked ? 'border-[1.5px] border-[var(--success)] bg-[var(--success)]' : 'border-[1.5px] border-[var(--cream-shadow)] bg-[var(--cream-1)]',
        )}
      >
        {checked ? <Icon kind="check" size={10} stroke={3} /> : null}
      </span>
      {dot ? <span className="ti-status-dot h-2 w-2 shrink-0" style={{ background: dot }} /> : null}
      {lead}
      <span className="min-w-0 flex-1">
        <span className="block text-[12.5px] font-semibold tracking-[-0.005em] text-[var(--ink-0)]">{label}</span>
        {sub ? <span className="block text-[10.5px] text-[var(--ink-3)]">{sub}</span> : null}
      </span>
      {count != null ? (
        <span className="v6-mono shrink-0 text-[10.5px] text-[var(--ink-3)]">{count}</span>
      ) : null}
    </div>
  );
}

export interface TriageFilterDropdownProps {
  trigger: (ctx: { open: boolean; toggle: () => void }) => React.ReactNode;
  children: (ctx: { close: () => void }) => React.ReactNode;
  align?: 'left' | 'right';
  minWidth?: number;
}

export function TriageFilterDropdown({
  trigger,
  children,
  align = 'left',
  minWidth = 224,
}: TriageFilterDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative min-w-0 shrink">
      {trigger({ open, toggle: () => setOpen((o) => !o) })}
      {open ? (
        <div
          className="ti-menu absolute z-[var(--z-dropdown)] max-h-[340px] overflow-auto"
          style={{
            [align]: 0,
            top: 'calc(100% + 5px)',
            minWidth,
          }}
        >
          {children({ close: () => setOpen(false) })}
        </div>
      ) : null}
    </div>
  );
}
